import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { InitialLoader } from './InitialLoader';
import { Construction, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CEREBRO_AUTH_STORAGE_KEY } from '@/integrations/supabase/client';

interface AppReadyGateProps {
  children: React.ReactNode;
}

/** True when a Supabase session blob exists in localStorage — used so we
 *  never flash the global loader on tab return / mid-session refreshes. */
function hasCachedSupabaseSession(): boolean {
  try {
    const raw = localStorage.getItem(CEREBRO_AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.user;
  } catch {
    return false;
  }
}

export function AppReadyGate({ children }: AppReadyGateProps) {
  const { loading: authLoading, user } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // Definitive policy:
  // 1. If we have a user → render immediately.
  // 2. If we have a cached session blob in localStorage → render immediately
  //    (auth will reconcile in background; if it ends up invalid, the route
  //    guards will redirect to /auth — much better than a global loader).
  // 3. If auth has finished loading → render (will route to /auth if no user).
  // 4. Only show the loader on truly fresh boot with no cache yet.
  const isReady = !!user || hasCachedSupabaseSession() || !authLoading;

  useEffect(() => {
    if (isReady) return;
    const timer = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(timer);
  }, [isReady]);

  if (isReady) return <>{children}</>;

  if (timedOut) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-6 max-w-sm text-center px-6">
          <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center">
            <Construction className="w-8 h-8 text-orange-500" />
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-bold text-foreground">
              Sistema em Manutenção
            </h1>
            <p className="text-sm text-muted-foreground">
              Estamos realizando atualizações para melhorar sua experiência. Tente novamente em alguns minutos.
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full">
            <Button
              onClick={() => window.location.reload()}
              className="gap-2"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar novamente
            </Button>

            <Button
              onClick={() => {
                localStorage.clear();
                window.location.href = '/auth';
              }}
              className="gap-2"
              variant="ghost"
              size="sm"
            >
              Sair e tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <InitialLoader />;
}
